import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import get from 'lodash/get';
import {
  DATA_DOG_ENV,
  DEFAULT_DD_DEPLOY_ENV,
  DEFAULT_DD_API_URL,
  DEFAULT_DD_SCHOOL_ID,
  DEFAULT_DD_IS_EP,
} from './constants';

const SCHOOL_ID = process.env.REACT_APP_SCHOOL_ID;
const checkList = ['password'];
const USER_CONTEXT_KEYS = ['id', 'email', 'userRole', 'schoolID'];
// REACT_APP_DEPLOY_ENV=staging and devy resolve to these.
const DEBUG_DD_ENVS = ['mfs-sn2-sit', 'lsh-sn2-sit'];

const maskInfo = (key, value) => {
  let maskedValue = value;
  if (checkList.indexOf(key) > -1) {
    maskedValue = '[REDACTED]';
  }
  return maskedValue;
};

export const isEnvAllowedForDatadog = () => {
  const APP_ENV = process?.env?.REACT_APP_DEPLOY_ENV ?? DEFAULT_DD_DEPLOY_ENV;
  const API_ENDPOINT =
    process?.env?.REACT_APP_REACT_API_URL ?? DEFAULT_DD_API_URL;
  const SCHOOL = process?.env?.REACT_APP_SCHOOL_ID ?? DEFAULT_DD_SCHOOL_ID;
  const isEP =
    process?.env?.REACT_APP_ENRICHMENT_PLATFORM === 'true' || DEFAULT_DD_IS_EP;
  let envName = '';

  if (APP_ENV === 'develop' || APP_ENV === 'development') {
    const match = API_ENDPOINT.match(/https:\/\/([^]+)\.api\.skool\.sg/);
    envName = match ? match[1] : '';
  } else if (APP_ENV === 'staging') {
    envName = 'stg';
  } else {
    if (String(SCHOOL) === '1') {
      envName = isEP ? 'mfs-ep-prod' : 'mfs-prod';
    }
    if (String(SCHOOL) === '2') {
      envName = 'lsh-prod';
    }
  }

  const dDEnv = DATA_DOG_ENV.find(env => env.env === envName);
  return dDEnv ? dDEnv?.value : null;
};

// Runs inside safely()'s catch, so it must not throw.
const warnInDebugEnv = error => {
  try {
    if (DEBUG_DD_ENVS.includes(isEnvAllowedForDatadog())) {
      console.warn('Datadog call failed and was swallowed', error);
    }
  } catch (warnError) {
    // nothing left to report with
  }
};

// Datadog runs on the login and logout paths, so a throw here breaks them.
const safely = fn => {
  try {
    return fn();
  } catch (error) {
    warnInDebugEnv(error);
    return undefined;
  }
};

export default class DatadogHandler {
  static init() {
    safely(() => this.initDatadog());
  }

  static initDatadog() {
    // Check if the current environment is listed among the allowed Datadog environments; else, do not initiate
    const ddEnv = isEnvAllowedForDatadog();
    const VERSION =
      process?.env?.REACT_APP_BITBUCKET_BRANCH ?? DEFAULT_DD_DEPLOY_ENV;
    const DD_CLIENT_TOKEN = process?.env?.REACT_APP_DD_CLIENT_TOKEN;
    const DD_APPLICATION_ID = process?.env?.REACT_APP_DD_APP_ID;

    if (DD_CLIENT_TOKEN && DD_APPLICATION_ID && ddEnv) {
      datadogLogs.init({
        clientToken: DD_CLIENT_TOKEN,
        service: 'sn-frontend',
        site: 'datadoghq.eu',
        forwardErrorsToLogs: true,
        isCollectingError: true,
        silentMultipleInit: true,
        sampleRate: 100,
        trackInteractions: true,
        useCrossSiteSessionCookie: true,
        useSecureSessionCookie: true,
        trackSessionAcrossSubdomains: true,
        trackUserInteractions: true,
        env: ddEnv,
        version: VERSION,
        beforeSend: log =>
          safely(() => {
            if (
              log?.origin === 'console' &&
              String(log?.message ?? '').includes('console error:')
            ) {
              return false;
            }
            return undefined;
          }),
      });

      // Check if it's production environment and URL includes '/pss'
      const shouldInitSessionReplay =
        process.env.REACT_APP_ENVIRONMENT === 'production' &&
        get(window, 'location.pathname', '').includes('/pss');

      datadogRum.init({
        enableExperimentalFeatures: ['feature_flags'],
        applicationId: DD_APPLICATION_ID,
        clientToken: DD_CLIENT_TOKEN,
        service: 'sn-frontend',
        site: 'datadoghq.eu',
        env: ddEnv,
        version: VERSION,
        sampleRate: 100,
        sessionReplaySampleRate: 100,
        trackResources: true,
        trackLongTasks: true,
        trackInteractions: true,
        trackUserInteractions: true,
        useSecureSessionCookie: true,
        beforeSend: (event, context) =>
          safely(() => {
            // Must precede anything that can throw, or safely() ships the email.
            const viewUrl = event?.view?.url;
            if (typeof viewUrl === 'string') {
              event.view.url = viewUrl.replace(/email=[^&]*/, 'email=REDACTED');
            }
            if (
              event?.type === 'error' &&
              String(event?.error?.message ?? '').includes('console error:')
            ) {
              return false;
            }
            // collect a RUM resource's response headers
            if (
              event?.type === 'resource' &&
              event?.resource?.type === 'fetch'
            ) {
              event.context = {
                ...event.context,
                responseHeaders: context?.response?.headers,
              };
            }
            return undefined;
          }),
      });
      if (shouldInitSessionReplay) {
        datadogRum.startSessionReplayRecording();
      }
    }
  }

  static setUserContext(userDetails) {
    return safely(() => {
      const userId = get(userDetails, 'ID');
      if (!userId) {
        return;
      }
      const user = {
        id: userId,
        email: get(userDetails, 'email'),
        userRole: get(userDetails, 'userRoleRelations.data[0].role.label'),
        schoolID: SCHOOL_ID,
      };
      datadogRum.setUser(user);
      datadogLogs.setUser(user);
      // setGlobalContext would replace the whole context, so write each property.
      USER_CONTEXT_KEYS.forEach(key => {
        datadogLogs.setGlobalContextProperty(key, user[key]);
      });
    });
  }

  static clearUserContext() {
    return safely(() => {
      datadogRum.clearUser();
      datadogLogs.clearUser();
      USER_CONTEXT_KEYS.forEach(key => {
        datadogLogs.removeGlobalContextProperty(key);
      });
    });
  }

  static addError(error) {
    return safely(() => datadogRum.addError(error));
  }

  static sendLog(message, context, type) {
    return safely(() => {
      // GraphQL error variables can be circular, so keep the log and drop context.
      const maskedData = safely(() =>
        JSON.parse(JSON.stringify(context, maskInfo))
      );
      datadogLogs.logger.log(message, maskedData ?? {}, type);
    });
  }

  static addAction(name, context = {}) {
    return safely(() => datadogRum.addAction(name, context));
  }
}
